import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { BrandListSection } from 'homepage/components/sections/BrandListSection';
import { CategoryListSection } from 'homepage/components/sections/CategoryListSection';
import { DetailedProductListSection } from 'homepage/components/sections/DetailedProductListSection';
import { ProductGridSection } from 'homepage/components/sections/ProductGridSection';
import { SpaceArticleSection } from 'homepage/components/sections/SpaceArticleSection';
import { ProductCarouselRowsSection } from 'homepage/components/sections/ProductCarouselRowsSection';
import { VerticalListCarouselSection } from 'homepage/components/sections/VerticalListCarouselSection';
import { getSearchLandingFeedSectionTrackingInfo } from 'homepage/utils/getSearchLandingFeedSectionTrackingInfo';
import { homepageDataCache } from 'homepage/utils/HomepageDataAndPositionCache';
import { SECTION_TYPES } from 'homepage/enums/contentConstants';
import { useStoreSelector } from 'store/hooks/storeHooks';

import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { IFeedSection } from 'homepage/types/Feed';

/**
 * [ContentIterator]
 * Renders the homepage layout based on the 'sections' of a ContentIterator feed.
 * @param {Array}  sections An array of modules, collectively representing a homepage.
 */

export const ContentIterator = ({
  constructorUserParams,
  draftId,
  draftUuid,
  sections,
  userSegmentIds,
}: {
  constructorUserParams?: IConstructorUserParams;
  draftId?: number;
  draftUuid?: string;
  sections: IFeedSection[];
  userSegmentIds: string[];
}) => {
  const router = useRouter();
  const { currentSearchLandingFeedData } = useStoreSelector((state) => state.homepage);

  // Map enumerated section types to their correct display component.
  const sectionComponentMap = Object.freeze({
    [SECTION_TYPES.BRAND_LIST_SECTION]: BrandListSection,
    [SECTION_TYPES.CATEGORY_LIST_SECTION]: CategoryListSection,
    [SECTION_TYPES.DETAILED_PRODUCT_LIST_SECTION]: DetailedProductListSection,
    [SECTION_TYPES.PRODUCT_CAROUSEL_ROWS_SECTION]: ProductCarouselRowsSection,
    [SECTION_TYPES.PRODUCT_GRID_SECTION]: ProductGridSection,
    [SECTION_TYPES.SPACE_ARTICLE_SECTION]: SpaceArticleSection,
    [SECTION_TYPES.VERTICAL_LIST_CAROUSEL_SECTION]: VerticalListCarouselSection,
  });

  useEffect(() => {
    const handleRouteChange = () => {
      homepageDataCache.write({
        scrollPosition: window?.scrollY || 0,
        currentFeed: currentSearchLandingFeedData,
      });
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [currentSearchLandingFeedData]);

  return sections && sections.length > 0 ? (
    <ContentIterator.Layout>
      {sections.map((section, sectionIndex) => {
        const { id, type, constructorResponse, uuid, ...sectionProps } = section;
        const ComponentType = sectionComponentMap[type];

        // if (type === SECTION_TYPES.BRAND_LIST_SECTION) {
        //   console.log(ComponentType, 'ComponentType for section type:', type);
        //   console.log(
        //     'sectionProps:',
        //     section?.segmentId && !userSegmentIds?.includes(section?.segmentId),
        //   );
        // }

        if (!ComponentType) return null;

        // if (
        //   section?.segmentId &&
        //   !userSegmentIds?.includes(section?.segmentId) &&
        //   type !== SECTION_TYPES.BRAND_LIST_SECTION
        // )
        //   return null;
        if (section?.segmentId && !userSegmentIds?.includes(section?.segmentId)) return null;

        return (
          <ComponentType
            key={uuid || id}
            sectionIndex={sectionIndex}
            id={uuid || id}
            type={type}
            moduleTrackingData={getSearchLandingFeedSectionTrackingInfo(
              section,
              sectionIndex,
              draftId,
              draftUuid,
            )}
            constructorResponse={constructorResponse}
            constructorUserParams={constructorUserParams}
            {...sectionProps}
          />
        );
      })}
    </ContentIterator.Layout>
  ) : null;
};

ContentIterator.Layout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
`;
